import React from "react";

const baseInput =
  "w-full px-4 py-3 rounded-xl border border-lw-navy/15 bg-white font-sans-ui text-base text-lw-navy placeholder:text-lw-muted/60 focus:outline-none focus:ring-2 focus:ring-lw-gold/50 focus:border-lw-gold transition-all";

export const NumberInput = ({
  label,
  value,
  onChange,
  prefix,
  suffix,
  min,
  max,
  step,
  error,
  helper,
  testId,
}) => (
  <label className="block">
    <span className="block font-sans-ui text-sm font-medium text-lw-navy mb-1.5">
      {label}
    </span>
    <div className="relative">
      {prefix && (
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lw-muted text-base pointer-events-none">
          {prefix}
        </span>
      )}
      <input
        type="number"
        inputMode="decimal"
        value={value === null || value === undefined ? "" : value}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "" ? "" : Number(v));
        }}
        min={min}
        max={max}
        step={step ?? "any"}
        data-testid={testId}
        className={`${baseInput} ${prefix ? "pl-8" : ""} ${suffix ? "pr-12" : ""} ${
          error ? "border-red-400 focus:ring-red-300 focus:border-red-400" : ""
        }`}
      />
      {suffix && (
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lw-muted text-sm pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
    {error ? (
      <span
        data-testid={testId ? `${testId}-error` : undefined}
        className="mt-1.5 block text-xs text-red-600"
      >
        {error}
      </span>
    ) : helper ? (
      <span className="mt-1.5 block text-xs text-lw-muted">{helper}</span>
    ) : null}
  </label>
);

export const SelectInput = ({ label, value, onChange, options, error, testId }) => (
  <label className="block">
    <span className="block font-sans-ui text-sm font-medium text-lw-navy mb-1.5">
      {label}
    </span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={`${baseInput} appearance-none pr-10 cursor-pointer ${
        error ? "border-red-400" : ""
      }`}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%236B7280' d='M5 6L0 0h10z'/%3E%3C/svg%3E\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 1rem center",
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    {error && (
      <span className="mt-1.5 block text-xs text-red-600">{error}</span>
    )}
  </label>
);

export const SliderInput = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  formatValue,
  testId,
}) => (
  <label className="block">
    <div className="flex items-center justify-between mb-1.5">
      <span className="font-sans-ui text-sm font-medium text-lw-navy">
        {label}
      </span>
      <span
        className="font-serif-display text-base text-lw-navy tabular-nums"
        data-testid={testId ? `${testId}-display` : undefined}
      >
        {formatValue ? formatValue(value) : value}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testId}
      className="lw-slider w-full h-2 rounded-full bg-lw-navy/10 accent-lw-gold cursor-pointer"
      style={{ accentColor: "#D4A24C" }}
    />
  </label>
);

export const RadioGroup = ({ label, value, onChange, options, testId }) => (
  <div>
    <span className="block font-sans-ui text-sm font-medium text-lw-navy mb-2">
      {label}
    </span>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            data-testid={testId ? `${testId}-${o.value}` : undefined}
            className={`px-3 py-3 rounded-xl border text-sm font-medium transition-all text-left ${
              selected
                ? "border-lw-gold bg-lw-gold/10 text-lw-navy shadow-sm"
                : "border-lw-navy/15 bg-white text-lw-navy/80 hover:border-lw-navy/30"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

export const Toggle = ({ label, value, onChange, testId, helper }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    data-testid={testId}
    className={`flex items-center justify-between w-full px-4 py-3 rounded-xl border text-left transition-all ${
      value
        ? "border-lw-gold bg-lw-gold/10"
        : "border-lw-navy/15 bg-white hover:border-lw-navy/30"
    }`}
  >
    <span>
      <span className="block font-sans-ui text-sm font-medium text-lw-navy">
        {label}
      </span>
      {helper && (
        <span className="block text-xs text-lw-muted mt-0.5">{helper}</span>
      )}
    </span>
    <span
      className={`relative w-11 h-6 rounded-full transition-colors ${
        value ? "bg-lw-gold" : "bg-lw-navy/15"
      }`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
          value ? "left-[1.375rem]" : "left-0.5"
        }`}
      />
    </span>
  </button>
);
